import React, { useRef, useState } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import EditableBlockWithIcon from '~/components/atom/EditableBlockWithIcon';
import {
  FlowAction,
  type Flow___ArgumentFragment,
  type Flow___Argument_PointerFragment,
} from '~/graphql/types';
import isPointerArgument from '~/components/page/Automation/v2/util/isPointerArgument';
import useBuilderContext from '../../../Builder/hooks/useBuilderContext';
import useRelativeMaps from '../../../Builder/hooks/useRelativeMaps';
import ActionLabelAsText from '../../../Builder/components/ActionLabelAsText';
import FieldLabel from '../../../FieldLabel';
import Selector from '../Selector';
import PositionWrapper from '../ConditionEditorV2/components/PositionWrapperV2';
import getPathForPointer from '../Selector/utils/getPathForPointer';
import {
  getPathRepresentation,
  text as labelText,
} from '../ConditionEditorV2/utils/getLabelForRep';
import type { FlowPath } from '../Selector/utils/getFieldsByPath';
import { ADDRESS_SUBJECT } from './constants';

export type Props = {
  dataTestId?: string;

  /** The action the field belongs to */
  actionId: string;

  /** Flow action type passed to the Selector */
  action: FlowAction;

  /** The currently selected address argument, if any */
  address: Flow___ArgumentFragment | null | undefined;

  /** Called when the user picks an address value from the Selector */
  onChange: (address: Flow___ArgumentFragment) => void;

  /** Placeholder when no address is selected */
  emptyValue?: string;
};

const text = {
  whichAddress: 'Gebruik adres uit:',
  emptyValue: 'Selecteer een adres',
};

/**
 * Lets the user pick a flow value of type address.
 * Currently used by ContactDetails and AssignContact.
 */
const AddressField: React.FCC<Props> = ({
  dataTestId,
  actionId,
  action,
  address,
  onChange,
  emptyValue = text.emptyValue,
}) => {
  const [showSelector, setShowSelector] = useState(false);
  const blockRef = useRef<HTMLSpanElement | null>(null);
  const maps = useRelativeMaps({ actionId });
  const { opts, primitiveInput, primitiveListInput } = useBuilderContext();

  // Derive the current pointer path/label for display
  let initialPath: FlowPath = [];
  let label: string | null = null;
  let hasError = false;

  if (address && isPointerArgument(address)) {
    const path = getPathForPointer(address.pointer, {
      subjectMap: maps.subjectMap,
      instanceMap: maps.instanceMap,
      directoryMap: maps.directoryMap,
      // ...(requireAddressSubject ? { validTypes: [ADDRESS_SUBJECT] } : {}),
      validTypes: [ADDRESS_SUBJECT],
    });

    if (path.error === undefined) {
      initialPath = path.result;
      label = getPathRepresentation(path.fullResult);
    } else {
      // Pointer no longer resolves – mark as error and show a fallback label
      hasError = true;
      label = labelText.invalidVariable;
    }
  }

  return (
    <JustificationContainer
      direction="column"
      width="100%"
      align="start"
      gap="xs"
    >
      <FieldLabel>{text.whichAddress}</FieldLabel>
      <EditableBlockWithIcon
        ref={blockRef}
        dataTestId={dataTestId}
        icon="map-pin"
        active={showSelector}
        hasError={hasError}
        onClick={e => {
          e.stopPropagation();
          setShowSelector(true);
        }}
      >
        {label ? <ActionLabelAsText str={label} /> : emptyValue}
      </EditableBlockWithIcon>

      {showSelector && (
        <PositionWrapper
          onClose={() => setShowSelector(false)}
          parentRef={blockRef.current}
        >
          <Selector
            onClose={() => setShowSelector(false)}
            onSelect={value => {
              if (
                value.__typename === 'Flow___Argument_File' ||
                value.__typename === 'Flow___InstanceCondition' ||
                value.__typename === 'Flow___SubjectFieldCondition'
              ) {
                return;
              }

              if (!isPointerArgument(value)) return;

              onChange({
                __typename: 'Flow___Argument_Pointer',
                pointer: {
                  __typename: 'Flow___Pointer',
                  path: value.pointer.path,
                },
              } satisfies Flow___Argument_PointerFragment);

              setShowSelector(false);
            }}
            inputPrimitives={primitiveInput}
            inputListPrimitives={primitiveListInput}
            argument={undefined}
            initialPath={initialPath}
            opts={{
              ...opts,
              ...maps,
              action,
              conditionType: 'condition',
              limitToInstanceSubjects: [ADDRESS_SUBJECT],
            }}
          />
        </PositionWrapper>
      )}
    </JustificationContainer>
  );
};

export default AddressField;
